// App.tsx
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AppShell from './components/AppShell';
import DicomPage from './pages/DicomPage';
import Search from './pages/Search';
import Characterizations from './pages/Characterizations';
import CharacterizationDetail from './pages/Characterizations/Detail';
import Overview from './pages/Overview';
import Studies from './pages/Studies';
import StudyDetail from './pages/StudyDetail';
import Dicoms from './pages/Dicoms';
import Login from './pages/auth/login/index';
import PrivateRoute from './components/PrivateRoute';
import PublicRoute from './components/PublicRoute';
import TokenHandler from './components/TokenHandler';
import { EncephalonProvider } from 'contexts/EncephalonContext';
import { ThemeModeProvider } from 'theme/ThemeMode';
import { useGetStudyDetailQuery } from 'services/exploreService';
import { Spin } from 'antd';

/**
 * Other iCardio tools deep-link to `/?dicom=…`, `/?study=…` and `/viewer?dicom=…` (optionally
 * with `t=`). Those links now open the DICOM page; a bare `/` shows the overview.
 */
const legacyTarget = (search: string) => {
  const p = new URLSearchParams(search);
  return { dicom: p.get('dicom'), study: p.get('study') };
};

/** `?study=` opens the study's first DICOM, as the old viewer did. */
const StudyFirstDicom: React.FC<{ study: string }> = ({ study }) => {
  const { currentData: data, error } = useGetStudyDetailQuery(study);
  if (error) return <Navigate to={`/studies/${encodeURIComponent(study)}`} replace />;
  if (!data) return <Spin style={{ margin: 48 }} />;
  const first = data.dicoms[0]?.icid;
  return (
    <Navigate
      to={first ? `/dicoms/${encodeURIComponent(first)}` : `/studies/${encodeURIComponent(study)}`}
      state={first ? { study: data.study.icid ?? data.study.uuid } : undefined}
      replace
    />
  );
};

const Legacy: React.FC<{ fallback: React.ReactElement }> = ({ fallback }) => {
  const { search } = useLocation();
  const { dicom, study } = legacyTarget(search);
  if (dicom) return <Navigate to={`/dicoms/${encodeURIComponent(dicom)}`} replace />;
  if (study) return <StudyFirstDicom study={study} />;
  return fallback;
};

const App: React.FC = () => {
  return (
    <ThemeModeProvider>
      <EncephalonProvider>
        <BrowserRouter>
          <TokenHandler />
          <Routes>
            <Route element={<PublicRoute />}>
              <Route path="/login" element={<Login />} />
            </Route>
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <AppShell />
                </PrivateRoute>
              }
            >
              <Route index element={<Legacy fallback={<Overview />} />} />
              <Route path="studies" element={<Studies />} />
              <Route path="studies/:key" element={<StudyDetail />} />
              <Route path="dicoms" element={<Dicoms />} />
              <Route path="dicoms/:icid" element={<DicomPage />} />
              <Route path="search" element={<Search />} />
              <Route path="characterizations" element={<Characterizations />} />
              <Route path="characterizations/:key" element={<CharacterizationDetail />} />
              <Route
                path="viewer"
                element={<Legacy fallback={<Navigate to="/dicoms" replace />} />}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </EncephalonProvider>
    </ThemeModeProvider>
  );
};

export default App;
